import { EMPTY_FIELD } from 'constants/emptyField';
import { Resources } from 'constants/resources';

import { Typography } from '@mui/material';
import { red } from '@mui/material/colors';
import { usePermissions } from 'hooks/usePermissions';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { FunctionField, RaRecord, ReferenceOneField } from 'react-admin';
import { Box } from 'shared/mui/Box';
import { DEFAULT_TIMEZONE } from 'shared/mui/DatePicker/config/defaultValues';
import { Link } from 'shared/mui/Link/Link';
import { Tooltip } from 'shared/mui/Tooltip';
import { CopyToClipboardLinkField, DateTimeField, TextField } from 'shared/react-admin/Fields';
import { Pagination } from 'shared/react-admin/Pagination/Pagination';
import { ReferenceField } from 'shared/react-admin/ReferenceFields';
import { Gateway } from 'types/embedded/gateway';
import { Method } from 'types/embedded/method';
import { Order } from 'types/embedded/order';
import { OrderProviderExternalError } from 'types/embedded/orderProviderExternalErrors';
import { convertReactAdminIdToHydraId } from 'utils';

import {
  ConfigurableDatagridStyled,
  ImageFieldStyled,
} from './OrderProviderExternalErrorsDatagrid.styled';

type Props = {
  timezone?: string;
  handleOpenGatewaySummaryDialog: (record: Gateway) => void;
};

const getRowSx = (record: OrderProviderExternalError) =>
  record?.providerExternalError?.isCritical ? { background: red[50] } : null;

export const OrderProviderExternalErrorsDatagrid: React.FC<Props> = ({
  handleOpenGatewaySummaryDialog,
  timezone = DEFAULT_TIMEZONE,
}) => {
  const translate = useTypedTranslate();
  const { hasResourceAccess } = usePermissions();

  return (
    <>
      <ConfigurableDatagridStyled bulkActionButtons={false} rowSx={getRowSx}>
        <TextField
          label={translate('app.orderProviderExternalErrors.datagrid.eventId')}
          sortBy="id"
          source="originId"
        />
        <DateTimeField
          label={translate('app.orderProviderExternalErrors.datagrid.createdAt')}
          resource="orders"
          sortBy="createdAt,id"
          source="createdAt"
          timezone={timezone}
        />
        <CopyToClipboardLinkField
          cellClassName="column-order"
          getLink={(record: RaRecord) =>
            `/orders/${encodeURIComponent(convertReactAdminIdToHydraId(record?.order?.id, Resources.Orders))}/show`
          }
          getValue={(record: Order) => record?.id}
          label={translate('app.orderProviderExternalErrors.datagrid.orderId')}
          sortable={false}
          source="order"
          target="_blank"
        />
        <ReferenceField
          cellClassName="column-direction"
          label={translate('app.table.direction')}
          reference="directions"
          sortable={false}
          source="order.direction"
        >
          <TextField source="description" />
        </ReferenceField>
        <ReferenceField
          cellClassName="column-category"
          emptyText={EMPTY_FIELD}
          label={translate('app.orderProviderExternalErrors.datagrid.category')}
          link={false}
          reference="external_error_categories"
          resource="external_error_categories"
          sortable={false}
          source="providerExternalError.category"
        >
          <TextField source="description" />
        </ReferenceField>
        <FunctionField
          cellClassName="column-isCritical"
          label={translate('app.orderProviderExternalErrors.datagrid.critical')}
          render={(record: RaRecord) =>
            `${record?.providerExternalError?.isCritical ? translate('app.common.yes') : translate('app.common.no')}`
          }
        />
        <TextField
          cellClassName="column-errorCode"
          emptyText={EMPTY_FIELD}
          label={translate('app.orderProviderExternalErrors.datagrid.declineCode')}
          sortable={false}
          source="providerExternalError.errorCode"
        />
        <TextField
          cellClassName="column-reason"
          emptyText={EMPTY_FIELD}
          label={translate('app.orderProviderExternalErrors.datagrid.declineReason')}
          sortable={false}
          source="providerExternalError.reason"
        />
        <TextField
          cellClassName="column-merchant"
          label={translate('app.table.merchant')}
          sortable={false}
          source="merchant.name"
        />
        <TextField
          cellClassName="column-provider"
          label={translate('app.table.provider')}
          sortable={false}
          source="provider.name"
        />
        <FunctionField
          label={translate('app.table.gateway')}
          render={(record: OrderProviderExternalError) => {
            return (
              <Tooltip
                arrow
                placement="bottom"
                title={translate(
                  'app.orderProviderExternalErrors.datagrid.declineReasonsByGateway',
                )}
              >
                <Link
                  onClick={() => handleOpenGatewaySummaryDialog(record?.order.gateway)}
                  sx={{ cursor: 'pointer' }}
                  underline="none"
                >
                  {record?.order?.gateway?.name}
                </Link>
              </Tooltip>
            );
          }}
          sortable={false}
          source="gateway"
        />
        <ReferenceOneField
          cellClassName="column-method"
          emptyText={EMPTY_FIELD}
          label={translate('app.table.method')}
          link={false}
          reference="methods"
          resource="methods"
          sortable={false}
          source="method.id"
          target="id"
        >
          <FunctionField
            render={(record: Method) => (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {hasResourceAccess(Resources.MethodLogos) && (
                  <ReferenceField reference="method_logos" resource="method_logos" source="logo">
                    <ImageFieldStyled source="filePath" />
                  </ReferenceField>
                )}
                <Typography variant="body2">{record?.name}</Typography>
              </Box>
            )}
          />
        </ReferenceOneField>
      </ConfigurableDatagridStyled>
      <Pagination sx={{ display: 'block' }} />
    </>
  );
};
